/**
 * ERD Canvas Stimulus Controller
 *
 * Renders an interactive entity relationship diagram using Cytoscape.js.
 * Selecting a node shows only that node and its direct relationships,
 * with outgoing edges in indigo and incoming edges in orange.
 * Tapping a neighbor switches focus to that node.
 */

import { Controller } from '@hotwired/stimulus';
import application from '~/stimulus_application';
import cytoscape, { type Core, type EventObject, type NodeSingular } from 'cytoscape';
// @ts-expect-error cytoscape-elk has no type declarations
import elk from 'cytoscape-elk';
import dagre, { type DagreLayoutOptions } from 'cytoscape-dagre';
import './canvas.module.css';

cytoscape.use(elk as cytoscape.Ext);
cytoscape.use(dagre);

interface ErdNode {
  id: string;
  label: string;
  categorySlug: string;
  categoryName: string;
  groupSlug: string | null;
  groupId: string | null;
  schemaId: string;
  propertyCount: number;
  relationshipCount: number;
  incomingRelationships: number;
}

interface ErdEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  cardinality: string;
}

interface ErdCategory {
  slug: string;
  name: string;
  nodeCount: number;
  color: string;
}

interface ErdGraph {
  nodes: ErdNode[];
  edges: ErdEdge[];
  categories: ErdCategory[];
}

const CATEGORY_HEX_COLORS: Record<string, string> = {
  blue: '#3b82f6',
  green: '#22c55e',
  purple: '#a855f7',
  indigo: '#6366f1',
  teal: '#14b8a6',
  pink: '#ec4899',
  orange: '#f97316',
  emerald: '#10b981',
  amber: '#f59e0b',
  cyan: '#06b6d4',
  violet: '#8b5cf6',
  rose: '#f43f5e',
  gray: '#6b7280',
  slate: '#64748b',
};

const CATEGORY_BG_COLORS: Record<string, string> = {
  blue: '#eff6ff',
  green: '#f0fdf4',
  purple: '#faf5ff',
  indigo: '#eef2ff',
  teal: '#f0fdfa',
  pink: '#fdf2f8',
  orange: '#fff7ed',
  emerald: '#ecfdf5',
  amber: '#fffbeb',
  cyan: '#ecfeff',
  violet: '#f5f3ff',
  rose: '#fff1f2',
  gray: '#f9fafb',
  slate: '#f8fafc',
};

export default class ErdCanvas extends Controller {
  static targets = [
    'container',
    'tooltip',
    'tooltipContent',
    'directionToggle',
    'algorithmToggle',
    'explorePanel',
    'exploreSearch',
    'exploreResults',
    'emptyState',
    'spinner',
  ];
  static values = {
    graph: Object,
    groupBasePath: String,
  };

  declare readonly containerTarget: HTMLElement;
  declare readonly tooltipTarget: HTMLElement;
  declare readonly tooltipContentTarget: HTMLElement;
  declare readonly directionToggleTarget: HTMLElement;
  declare readonly algorithmToggleTarget: HTMLElement;
  declare readonly explorePanelTarget: HTMLElement;
  declare readonly exploreSearchTarget: HTMLInputElement;
  declare readonly exploreResultsTarget: HTMLElement;
  declare readonly emptyStateTarget: HTMLElement;
  declare readonly spinnerTarget: HTMLElement;
  declare graphValue: ErdGraph;
  declare groupBasePathValue: string;

  private cy: Core | null = null;
  private layoutDirection: 'RIGHT' | 'DOWN' = 'DOWN';
  private layoutAlgorithm: 'dagre' | 'elk' = 'dagre';
  private selectedNodeId: string | null = null;

  /**
   * Initialize the Cytoscape instance and show the search panel
   */
  connect() {
    this.initializeCytoscape();
    this.explorePanelTarget.classList.remove('hidden');
    this.exploreSearchTarget.focus();
  }

  /**
   * Clean up when disconnecting
   */
  disconnect() {
    this.cy?.destroy();
    this.cy = null;
  }

  // --- Zoom Controls ---

  zoomIn() {
    if (!this.cy) return;
    this.cy.zoom({ level: this.cy.zoom() * 1.2, renderedPosition: this.centerPosition() });
  }

  zoomOut() {
    if (!this.cy) return;
    this.cy.zoom({ level: this.cy.zoom() / 1.2, renderedPosition: this.centerPosition() });
  }

  fitToScreen() {
    this.cy?.fit(undefined, 40);
  }

  toggleDirection() {
    this.layoutDirection = this.layoutDirection === 'DOWN' ? 'RIGHT' : 'DOWN';
    this.directionToggleTarget.textContent = this.layoutDirection === 'DOWN' ? 'TB' : 'LR';
    if (this.selectedNodeId) {
      this.showSpinner();
      this.runLayout();
    }
  }

  /**
   * Toggle between Dagre and ELK layout algorithms
   */
  toggleAlgorithm() {
    this.layoutAlgorithm = this.layoutAlgorithm === 'dagre' ? 'elk' : 'dagre';
    this.algorithmToggleTarget.textContent = this.layoutAlgorithm === 'dagre' ? 'Dagre' : 'ELK';
    if (this.selectedNodeId) {
      this.showSpinner();
      this.runLayout();
    }
  }

  // --- Explore Search ---

  /**
   * Search for nodes to focus on
   */
  searchExplore() {
    const query = this.exploreSearchTarget.value.toLowerCase().trim();
    this.renderExploreResults(query);
  }

  /**
   * Focus on a node selected from the search results
   */
  addExploreNode(event: Event) {
    const nodeId = (event.currentTarget as HTMLElement).dataset.nodeId!;
    if (!this.cy || !nodeId) return;

    this.exploreSearchTarget.value = '';
    this.exploreResultsTarget.classList.add('hidden');
    this.focusOnNode(nodeId);
  }

  // --- Tooltip ---

  /**
   * Tooltip mouse leave — always hide
   */
  tooltipLeave() {
    this.hideTooltip();
  }

  // --- Private Methods ---

  private centerPosition() {
    const container = this.containerTarget;
    return { x: container.clientWidth / 2, y: container.clientHeight / 2 };
  }

  /**
   * Initialize an empty Cytoscape instance
   */
  private initializeCytoscape() {
    const graph = this.graphValue as ErdGraph;

    this.cy = cytoscape({
      container: this.containerTarget,
      elements: [],
      style: this.buildStylesheet(graph),
      layout: { name: 'preset' },
      minZoom: 0.1,
      maxZoom: 3,
      wheelSensitivity: 0.3,
    });

    this.setupEventHandlers();
  }

  /**
   * Focus on a node: clear canvas, load node + direct neighbors,
   * add only edges between selected node and its neighbors, highlight.
   */
  private focusOnNode(nodeId: string) {
    if (!this.cy) return;

    const graph = this.graphValue as ErdGraph;
    const selectedData = graph.nodes.find((n) => n.id === nodeId);
    if (!selectedData) return;

    // Hide empty state on first selection
    this.emptyStateTarget.classList.add('hidden');

    // Clear canvas
    this.cy.elements().remove();

    // Load selected node
    this.cy.add({ data: this.buildNodeData(graph, selectedData) });

    // Find direct edges involving the selected node only
    const directEdges = graph.edges.filter((e) => e.source === nodeId || e.target === nodeId);

    // Load direct neighbors (skip uncategorized)
    const neighborIds = new Set(
      directEdges.map((e) => (e.source === nodeId ? e.target : e.source))
    );
    neighborIds.forEach((id) => {
      const nodeData = graph.nodes.find((n) => n.id === id && n.categorySlug !== 'uncategorized');
      if (nodeData) this.cy!.add({ data: this.buildNodeData(graph, nodeData) });
    });

    // Add only selected↔neighbor edges (no neighbor-to-neighbor edges)
    const loadedIds = new Set(this.cy.nodes().map((n) => n.id()));
    directEdges
      .filter((e) => loadedIds.has(e.source) && loadedIds.has(e.target))
      .forEach((e) => {
        this.cy!.add({
          data: {
            id: e.id,
            source: e.source,
            target: e.target,
            label: e.label,
            cardinality: e.cardinality,
          },
        });
      });

    // Apply highlight to selected node
    this.selectedNodeId = nodeId;
    this.highlightNode(this.cy.getElementById(nodeId));

    this.showSpinner();
    this.runLayout();
  }

  /**
   * Render explore search results dropdown
   */
  private renderExploreResults(query: string) {
    const graph = this.graphValue as ErdGraph;

    if (!query) {
      this.exploreResultsTarget.classList.add('hidden');
      return;
    }

    const results = graph.nodes.filter(
      (n) =>
        n.categorySlug !== 'uncategorized' &&
        (n.label.toLowerCase().includes(query) || n.id.includes(query))
    );

    if (results.length === 0) {
      this.exploreResultsTarget.classList.add('hidden');
      return;
    }

    this.exploreResultsTarget.classList.remove('hidden');
    this.exploreResultsTarget.innerHTML = results
      .slice(0, 8)
      .map((node) => {
        const isSelected = node.id === this.selectedNodeId;
        const color = this.categoryHexColor(graph, node.categorySlug);
        return `
        <button type="button"
          data-node-id="${node.id}"
          data-action="click->${this.identifier}#addExploreNode"
          class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${isSelected ? 'text-gray-400' : 'text-gray-900'}">
          <span class="w-2 h-2 rounded-full flex-shrink-0" style="background:${color}"></span>
          <span class="font-medium">${node.label}</span>
          <span class="text-xs text-gray-400 ml-1">${node.categoryName}</span>
          ${isSelected ? '<span class="ml-auto text-xs text-gray-400">selected</span>' : ''}
        </button>`;
      })
      .join('');
  }

  /**
   * Build the data object for a single node element
   */
  private buildNodeData(graph: ErdGraph, node: ErdNode) {
    return {
      id: node.id,
      label: node.label,
      categorySlug: node.categorySlug,
      categoryName: node.categoryName,
      groupSlug: node.groupSlug,
      groupId: node.groupId,
      schemaId: node.schemaId,
      propertyCount: node.propertyCount,
      relationshipCount: node.relationshipCount,
      incomingRelationships: node.incomingRelationships,
      color: this.categoryHexColor(graph, node.categorySlug),
      bgColor: this.categoryBgColor(graph, node.categorySlug),
    };
  }

  /**
   * Build Cytoscape stylesheet
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private buildStylesheet(_graph: ErdGraph): any[] {
    return [
      {
        selector: 'node',
        style: {
          label: 'data(label)',
          'text-valign': 'center',
          'text-halign': 'center',
          'font-size': '11px',
          'font-family': 'Inter, system-ui, sans-serif',
          'font-weight': 500,
          color: '#1f2937',
          'background-color': 'data(bgColor)',
          'border-width': 2,
          'border-color': 'data(color)',
          shape: 'roundrectangle',
          width: 'label',
          height: 30,
          'padding-left': '12px',
          'padding-right': '12px',
          'text-wrap': 'none',
          cursor: 'pointer',
        },
      },
      {
        selector: 'node:active',
        style: { 'overlay-opacity': 0.1 },
      },
      {
        selector: 'node.highlighted',
        style: { 'border-width': 3, 'z-index': 10 },
      },
      {
        selector: 'edge',
        style: {
          width: 1,
          'line-color': '#d1d5db',
          'target-arrow-color': '#d1d5db',
          'target-arrow-shape': 'triangle',
          'arrow-scale': 0.6,
          'curve-style': 'bezier',
          label: '',
        },
      },
      {
        selector: 'edge.highlighted-outgoing',
        style: {
          width: 2,
          'line-color': '#6366f1',
          'target-arrow-color': '#6366f1',
          'arrow-scale': 0.8,
          label: 'data(label)',
          'font-size': '9px',
          'font-family': 'Inter, system-ui, sans-serif',
          color: '#6366f1',
          'text-rotation': 'none',
          'z-index': 10,
        },
      },
      {
        selector: 'edge.highlighted-incoming',
        style: {
          width: 2,
          'line-color': '#f97316',
          'target-arrow-color': '#f97316',
          'arrow-scale': 0.8,
          label: 'data(label)',
          'font-size': '9px',
          'font-family': 'Inter, system-ui, sans-serif',
          color: '#f97316',
          'text-rotation': 'none',
          'z-index': 10,
        },
      },
      {
        selector: 'edge.has-many',
        style: { 'line-style': 'solid', 'source-arrow-shape': 'none' },
      },
    ];
  }

  /**
   * Set up Cytoscape event handlers
   */
  private setupEventHandlers() {
    if (!this.cy) return;

    // Hover: show tooltip
    this.cy.on('mouseover', 'node', (event: EventObject) => {
      const node = event.target as NodeSingular;
      this.showTooltip(event, node);
    });

    this.cy.on('mouseout', 'node', () => {
      this.hideTooltip();
    });

    // Tap: switch focus to tapped node (no-op if already selected)
    this.cy.on('tap', 'node', (event: EventObject) => {
      const node = event.target as NodeSingular;
      if (node.id() === this.selectedNodeId) return;
      this.hideTooltip();
      this.focusOnNode(node.id());
    });
  }

  /**
   * Highlight the selected node with colored incoming/outgoing edges
   */
  private highlightNode(node: NodeSingular) {
    if (!this.cy) return;
    node.addClass('highlighted');
    node.outgoers('edge').addClass('highlighted-outgoing');
    node.incomers('edge').addClass('highlighted-incoming');
  }

  /**
   * Show tooltip near a node
   */
  private showTooltip(_event: EventObject, node: NodeSingular) {
    const data = node.data();
    const groupSlug = data.groupSlug;
    const url = groupSlug ? this.groupBasePathValue.replace('__GROUP_SLUG__', groupSlug) : null;
    const isSelected = node.id() === this.selectedNodeId;

    this.tooltipContentTarget.innerHTML = `
      <div class="font-semibold text-sm text-gray-900 mb-1">${data.label}</div>
      <div class="text-xs text-gray-500 mb-1">${data.categoryName}</div>
      <div class="text-xs text-gray-600 space-y-0.5">
        <div>${data.propertyCount} properties</div>
        <div><span style="color:#6366f1">&#9658;</span> ${data.relationshipCount} outgoing &nbsp;<span style="color:#f97316">&#9658;</span> ${data.incomingRelationships} incoming</div>
      </div>
      ${!isSelected ? '<div class="text-xs text-blue-600 mt-1">Click to explore relationships</div>' : ''}
      ${
        url
          ? `<div class="mt-2"><a href="${url}" data-turbo-frame="main_content" class="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
          View in docs
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </a></div>`
          : ''
      }
    `;

    const renderedPosition = node.renderedPosition();
    const containerRect = this.containerTarget.getBoundingClientRect();
    const parentRect = this.element.getBoundingClientRect();

    this.tooltipTarget.style.left = `${containerRect.left - parentRect.left + renderedPosition.x + 20}px`;
    this.tooltipTarget.style.top = `${containerRect.top - parentRect.top + renderedPosition.y - 20}px`;
    this.tooltipTarget.classList.remove('hidden');
  }

  /**
   * Hide the tooltip
   */
  private hideTooltip() {
    this.tooltipTarget.classList.add('hidden');
  }

  /**
   * Run the layout algorithm on all visible elements
   */
  private runLayout() {
    if (!this.cy) return;

    let layout: cytoscape.Layouts;

    if (this.layoutAlgorithm === 'dagre') {
      const dagreOptions: DagreLayoutOptions = {
        name: 'dagre',
        rankDir: this.layoutDirection === 'RIGHT' ? 'LR' : 'TB',
        nodeSep: 60,
        rankSep: 100,
        edgeSep: 20,
        fit: true,
        padding: 40,
      };
      layout = this.cy.layout(dagreOptions as cytoscape.LayoutOptions);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const elkOptions: any = {
        name: 'elk',
        elk: {
          algorithm: 'layered',
          'elk.direction': this.layoutDirection,
          'elk.spacing.nodeNode': '60',
          'elk.layered.spacing.nodeNodeBetweenLayers': '100',
          'elk.layered.spacing.edgeNodeBetweenLayers': '50',
          'elk.spacing.edgeNode': '40',
          'elk.edgeRouting': 'SPLINES',
          'elk.layered.mergeEdges': 'false',
          'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
          'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
          'elk.separateConnectedComponents': 'false',
        },
        fit: true,
        padding: 40,
      };
      layout = this.cy.layout(elkOptions);
    }

    layout.on('layoutstop', () => {
      this.hideSpinner();
    });

    layout.run();
  }

  /**
   * Show loading spinner
   */
  private showSpinner() {
    this.spinnerTarget.classList.remove('hidden');
  }

  /**
   * Hide loading spinner
   */
  private hideSpinner() {
    this.spinnerTarget.classList.add('hidden');
  }

  /**
   * Get hex color for a category
   */
  private categoryHexColor(graph: ErdGraph, slug: string): string {
    const cat = graph.categories.find((c) => c.slug === slug);
    return CATEGORY_HEX_COLORS[cat?.color || 'gray'] || CATEGORY_HEX_COLORS.gray;
  }

  /**
   * Get background color for a category
   */
  private categoryBgColor(graph: ErdGraph, slug: string): string {
    const cat = graph.categories.find((c) => c.slug === slug);
    return CATEGORY_BG_COLORS[cat?.color || 'gray'] || CATEGORY_BG_COLORS.gray;
  }
}

// Extend Window for Turbo
declare global {
  interface Window {
    Turbo?: {
      visit(url: string): void;
    };
  }
}

application.register('openapi-ui--erd--canvas', ErdCanvas);
